import React, { useState } from "react";
import { Form, Input, Select, Checkbox, Button, message } from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Purebrand } from "assets/images";
import { Container, Navbar } from "react-bootstrap";
import "./ExtraGuestForm.css";

import {
  GUEST_FORM_LABELS,
  GUEST_FORM_PLACEHOLDERS,
  GUEST_FORM_VALIDATION_ERRORS,
  CHECKBOX_TEXT
} from "../PublicUser/constants";

/**
 * ExtraGuestForm
 *
 * Renders a registration form for extra guests joining a live demo.
 * Collects guest contact details, product category, and consent for
 * communication before submitting the data to the parent handler.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onSubmit - Callback invoked with form values on submit
 * @param {Array<Object>} props.storeCategories - List of available product categories
 * @param {Object} props.host - Host details used for display
 * @returns {JSX.Element}
 */

export default function ExtraGuestForm({ onSubmit, storeCategories = [], host }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const receiveTexts = Form.useWatch(["guest", "message_opt_in"], form);

  /**
   * Handles extra guest form submission.
   *
   * Triggers the parent submit handler and manages
   * loading and error state for the form.
   *
   * @param {Object} values - Submitted form values
   * @returns {Promise<void>}
   */
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      await onSubmit(values);
    } catch (e) {
      messageApi.error(e?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="live-meeting-form-container">
      {contextHolder}

      {/* HEADER (Exact copy from LiveMeeting.js) */}
      <div className="custom-live-header">
        <header>
          <Navbar bg="transparent">
            <Container>
              <Navbar.Brand className="pb-logo-header">
                <img
                  src={Purebrand}
                  className="Header-img pb-logo-header-public-guest-details"
                  alt="logo"
                />
              </Navbar.Brand>
            </Container>
          </Navbar>
        </header>
      </div>

      {/* FORM */}
      <div className="form-container">
        <h1 className="meeting-form-title">
          Enter your information to meet with {host?.first_name || "host"}
        </h1>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            receiveTexts: false
          }}
        >
          {/* FULL NAME */}
          <Form.Item
            label={GUEST_FORM_LABELS.FULL_NAME}
            name={["guest", "full_name"]}
            rules={[{ required: true, message: GUEST_FORM_VALIDATION_ERRORS.FULL_NAME }]}
          >
            <Input placeholder={GUEST_FORM_PLACEHOLDERS.FULL_NAME} />
          </Form.Item>

          {/* PHONE */}
          <Form.Item
            label={GUEST_FORM_LABELS.PHONE_NUMBER}
            name={["guest", "phone_number"]}
            rules={[{ required: true, message: GUEST_FORM_VALIDATION_ERRORS.PHONE_NUMBER }]}
          >
            <PhoneInput
              country="us"
              countryCodeEditable={false}
              placeholder={GUEST_FORM_PLACEHOLDERS.PHONE_NUMBER}
            />
          </Form.Item>

          {/* EMAIL */}
          <Form.Item
            label={GUEST_FORM_LABELS.EMAIL}
            name={["guest", "email"]}
            rules={[
              { required: true, message: GUEST_FORM_VALIDATION_ERRORS.EMAIL },
              { type: "email", message: GUEST_FORM_VALIDATION_ERRORS.VALID_EMAIL }
            ]}
          >
            <Input placeholder={GUEST_FORM_PLACEHOLDERS.EMAIL} />
          </Form.Item>

          {/* CATEGORY */}
          <Form.Item label={GUEST_FORM_LABELS.PRODUCT_CATEGORY}>
            <div className="host-category-list">
              {storeCategories?.length > 0 ? (
                <ul style={{ marginBottom: 0 }}>
                  {storeCategories.map((category) => (
                    <li key={category.id}>{category.name}</li>
                  ))}
                </ul>
              ) : (
                <span>No categories available</span>
              )}
            </div>
          </Form.Item>

          {/* CHECKBOX */}
          <Form.Item name={["guest", "message_opt_in"]} valuePropName="checked">
            <Checkbox>{CHECKBOX_TEXT}</Checkbox>
          </Form.Item>

          {/* BUTTON */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              disabled={!receiveTexts}
            >
              Meet {host?.first_name}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}
